<script setup>
// Sales Projects — the client-presentation counterpart to
// ../projects/ProjectList.vue (the admin control center, unchanged).
// Reuses the exact same account-scoped, already-authorized endpoint
// (GET /nexceo/projects — Nexceo::AccessPolicy#scope_projects) so a Sales
// Agent sees only their assigned project(s), a Team Leader sees the
// project(s) they manage, and an Administrator sees every project — with no
// second authorization system and no duplicate project data.
//
// UI/UX redesign per docs/project.png: search/status-filter/sort/grid-list
// are all client-side presentation controls over the same `projects` array
// already loaded below — no new endpoints, no re-scoping. Sort uses the
// real `created_at` the backend already returns; status filter uses the
// real, existing status values (STATUS_META below, unchanged). The
// reference's "Total Units" reads "-" because the project list response
// has no unit-count field (see Nexceo::ProjectsController#project_json) —
// that's preserved honestly rather than querying inventory just for this
// card or fabricating a number. Likewise the reference's card-corner "…"
// menu isn't reproduced: this page has no supported per-project actions to
// put in it, and the task's own instructions are explicit not to invent
// any.
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import nexceoProjectsAPI from 'dashboard/api/nexceo/projects';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const projects = ref([]);
const isLoading = ref(false);
const error = ref(null);

const STATUS_META = {
  under_construction: {
    label: 'Under Construction',
    icon: 'i-lucide-hard-hat',
    class:
      'bg-yellow-50 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400',
  },
  ready_to_move: {
    label: 'Ready to Move',
    icon: 'i-lucide-check-circle',
    class:
      'bg-green-50 text-green-800 dark:bg-green-800/20 dark:text-green-400',
  },
  sold: {
    label: 'Sold',
    icon: 'i-lucide-lock',
    class: 'bg-n-alpha-2 text-n-slate-8',
  },
};
const DEFAULT_STATUS_META = { label: null, icon: 'i-lucide-tag', class: 'bg-n-alpha-2 text-n-slate-8' };

function statusMeta(project) {
  const meta = STATUS_META[project.status] ?? DEFAULT_STATUS_META;
  return meta.label ? meta : { ...meta, label: project.status };
}

function locationLabel(project) {
  return (
    [project.city, project.state].filter(Boolean).join(', ') ||
    project.location ||
    ''
  );
}

function formatPriceRange(project) {
  if (!project.price_min && !project.price_max) return null;
  const sym = project.currency === 'INR' ? '₹' : project.currency || '';
  const fmt = v => {
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000) return `${(v / 100000).toFixed(0)}L`;
    return Number(v).toLocaleString();
  };
  if (project.price_min && project.price_max) {
    return `${sym}${fmt(project.price_min)} – ${sym}${fmt(project.price_max)}`;
  }
  return `${sym}${fmt(project.price_min || project.price_max)} onwards`;
}

async function loadProjects() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await nexceoProjectsAPI.get();
    projects.value = Array.isArray(data) ? data : (data?.projects ?? []);
  } catch {
    error.value = 'Failed to load projects. Please try again.';
  } finally {
    isLoading.value = false;
  }
}

function openProject(project) {
  router.push(
    accountScopedRoute('nexceo_sales_project_show', {
      id: project.id,
      tab: 'overview',
    })
  );
}

onMounted(loadProjects);

// ── Presentation-only controls (client-side over the already-loaded,
// already-authorized `projects` array — no new API calls) ─────────────────
const searchTerm = ref('');
const statusFilter = ref('');
const sortOrder = ref('newest');
const viewMode = ref('grid');

const statusFilterOptions = computed(() =>
  Object.keys(STATUS_META).filter(key =>
    projects.value.some(p => p.status === key)
  )
);

const filteredProjects = computed(() => {
  let list = projects.value;

  const q = searchTerm.value.trim().toLowerCase();
  if (q) {
    list = list.filter(p =>
      [p.name, p.city, p.state, p.location]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(q))
    );
  }

  if (statusFilter.value) {
    list = list.filter(p => p.status === statusFilter.value);
  }

  return [...list].sort((a, b) => {
    const da = new Date(a.created_at).getTime() || 0;
    const db = new Date(b.created_at).getTime() || 0;
    return sortOrder.value === 'oldest' ? da - db : db - da;
  });
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Slim top bar -->
    <div
      class="flex items-center justify-between px-6 h-14 border-b border-n-weak bg-n-background shrink-0"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-building-2 size-4 text-woot-500" />
        <h1 class="text-base font-semibold text-n-slate-12">Projects</h1>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center gap-2 flex-1"
    >
      <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
      <span class="text-sm text-n-slate-9">Loading projects</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center justify-center flex-1">
      <div class="text-center">
        <span
          class="i-lucide-alert-circle size-8 text-red-500 mx-auto block mb-2"
        />
        <p class="text-sm text-n-slate-11">{{ error }}</p>
        <button
          class="mt-3 text-sm text-woot-500 hover:underline"
          @click="loadProjects"
        >
          Try again
        </button>
      </div>
    </div>

    <!-- Empty state (no projects at all) -->
    <div
      v-else-if="!projects.length"
      class="flex items-center justify-center flex-1"
    >
      <div class="text-center max-w-sm">
        <div
          class="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
        >
          <span class="i-lucide-building-2 size-7 text-woot-500" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-1">
          No projects yet
        </h3>
        <p class="text-sm text-n-slate-9">
          You haven't been assigned to a project yet. Ask an admin to add you
          as a team member.
        </p>
      </div>
    </div>

    <!-- Page body -->
    <div v-else class="flex-1 overflow-auto p-6">
      <!-- Header: title/subtitle + controls -->
      <div
        class="flex items-start justify-between gap-4 flex-wrap mb-6"
      >
        <div>
          <h2 class="text-2xl font-bold text-n-slate-12">
            Real Estate Projects
          </h2>
          <p class="text-sm text-n-slate-9 mt-1">
            Track and manage all your projects in one place.
          </p>
        </div>

        <div class="flex items-center gap-2 flex-wrap">
          <div class="relative">
            <span
              class="i-lucide-search size-4 text-n-slate-7 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            />
            <input
              v-model="searchTerm"
              type="text"
              placeholder="Search projects..."
              class="list-field !pl-9 !pr-3 w-52"
            />
          </div>

          <select v-model="statusFilter" class="list-field appearance-none !px-3 w-36">
            <option value="">All Status</option>
            <option v-for="key in statusFilterOptions" :key="key" :value="key">
              {{ STATUS_META[key].label }}
            </option>
          </select>

          <select v-model="sortOrder" class="list-field appearance-none !px-3 w-36">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>

          <div class="flex items-center !border !border-solid border-n-weak rounded-lg overflow-hidden shrink-0">
            <button
              type="button"
              class="flex items-center justify-center size-9 transition-colors"
              :class="
                viewMode === 'grid'
                  ? 'bg-woot-500 text-white'
                  : 'bg-n-background text-n-slate-8 hover:bg-n-alpha-2'
              "
              title="Grid view"
              @click="viewMode = 'grid'"
            >
              <span class="i-lucide-layout-grid size-4" />
            </button>
            <button
              type="button"
              class="flex items-center justify-center size-9 transition-colors !border-l !border-solid border-n-weak"
              :class="
                viewMode === 'list'
                  ? 'bg-woot-500 text-white'
                  : 'bg-n-background text-n-slate-8 hover:bg-n-alpha-2'
              "
              title="List view"
              @click="viewMode = 'list'"
            >
              <span class="i-lucide-list size-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- No results after filtering -->
      <div
        v-if="!filteredProjects.length"
        class="flex flex-col items-center justify-center text-center py-16"
      >
        <span class="i-lucide-search-x size-8 text-n-slate-6 mb-2" />
        <p class="text-sm font-medium text-n-slate-10">
          No projects match your search or filter.
        </p>
      </div>

      <!-- Grid view -->
      <div
        v-else-if="viewMode === 'grid'"
        class="grid gap-5 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]"
      >
        <div
          v-for="project in filteredProjects"
          :key="project.id"
          class="flex flex-col rounded-2xl border border-n-weak bg-n-background overflow-hidden hover:shadow-lg hover:border-woot-300 dark:hover:border-woot-700 transition-all group"
        >
          <!-- Hero image with status badge overlay -->
          <button
            type="button"
            class="relative h-48 w-full flex items-center justify-center shrink-0 overflow-hidden"
            :class="
              project.hero_image_url
                ? ''
                : 'bg-gradient-to-br from-woot-500/20 via-woot-400/10 to-transparent'
            "
            @click="openProject(project)"
          >
            <img
              v-if="project.hero_image_url"
              :src="project.hero_image_url"
              alt=""
              class="absolute inset-0 size-full object-cover"
            />
            <span
              v-else
              class="i-lucide-building-2 size-10 text-woot-500/60 group-hover:text-woot-500 transition-colors"
            />
            <span
              class="absolute left-3 bottom-3 flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm"
              :class="statusMeta(project).class"
            >
              <span :class="statusMeta(project).icon" class="size-3.5" />
              {{ statusMeta(project).label }}
            </span>
          </button>

          <div class="flex flex-col gap-3 p-4 flex-1">
            <div>
              <h3 class="text-base font-bold text-n-slate-12 leading-snug">
                {{ project.name }}
              </h3>
              <p
                v-if="locationLabel(project)"
                class="flex items-center gap-1 text-xs text-n-slate-9 mt-1"
              >
                <span class="i-lucide-map-pin size-3 shrink-0" />
                {{ locationLabel(project) }}
              </p>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div class="flex items-start gap-2">
                <span
                  class="i-lucide-coins size-4 text-n-slate-7 shrink-0 mt-0.5"
                />
                <div>
                  <p class="text-[11px] text-n-slate-7">Project Value</p>
                  <p class="text-sm font-semibold text-woot-600 dark:text-woot-400">
                    {{ formatPriceRange(project) || '—' }}
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-2">
                <span
                  class="i-lucide-building size-4 text-n-slate-7 shrink-0 mt-0.5"
                />
                <div>
                  <p class="text-[11px] text-n-slate-7">Total Units</p>
                  <p class="text-sm font-semibold text-n-slate-11">-</p>
                </div>
              </div>
            </div>

            <div class="border-t border-n-weak pt-3 mt-auto">
              <button
                type="button"
                class="flex items-center gap-1.5 text-xs font-semibold text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-500/10 hover:bg-woot-100 dark:hover:bg-woot-500/20 rounded-lg px-3 py-1.5 transition-all"
                @click="openProject(project)"
              >
                Open Project
                <span class="i-lucide-arrow-right size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- List view -->
      <div v-else class="flex flex-col gap-3">
        <button
          v-for="project in filteredProjects"
          :key="project.id"
          type="button"
          class="flex items-center gap-4 text-left rounded-xl border border-n-weak bg-n-background p-3 hover:shadow-md hover:border-woot-300 dark:hover:border-woot-700 transition-all group"
          @click="openProject(project)"
        >
          <div
            class="relative size-16 rounded-lg overflow-hidden shrink-0 flex items-center justify-center"
            :class="
              project.hero_image_url
                ? ''
                : 'bg-gradient-to-br from-woot-500/20 via-woot-400/10 to-transparent'
            "
          >
            <img
              v-if="project.hero_image_url"
              :src="project.hero_image_url"
              alt=""
              class="absolute inset-0 size-full object-cover"
            />
            <span
              v-else
              class="i-lucide-building-2 size-6 text-woot-500/60"
            />
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="text-sm font-bold text-n-slate-12 truncate">
                {{ project.name }}
              </h3>
              <span
                class="shrink-0 flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                :class="statusMeta(project).class"
              >
                <span :class="statusMeta(project).icon" class="size-3" />
                {{ statusMeta(project).label }}
              </span>
            </div>
            <p
              v-if="locationLabel(project)"
              class="flex items-center gap-1 text-xs text-n-slate-9 mt-0.5"
            >
              <span class="i-lucide-map-pin size-3 shrink-0" />
              {{ locationLabel(project) }}
            </p>
          </div>

          <div class="hidden sm:block text-right shrink-0">
            <p class="text-[11px] text-n-slate-7">Project Value</p>
            <p class="text-sm font-semibold text-woot-600 dark:text-woot-400">
              {{ formatPriceRange(project) || '—' }}
            </p>
          </div>
          <div class="hidden md:block text-right shrink-0 w-20">
            <p class="text-[11px] text-n-slate-7">Total Units</p>
            <p class="text-sm font-semibold text-n-slate-11">-</p>
          </div>

          <div
            class="hidden lg:flex items-center gap-1.5 text-xs font-semibold text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-500/10 rounded-lg px-3 py-1.5 shrink-0"
          >
            Open Project
            <span class="i-lucide-arrow-right size-3.5" />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * _base.scss has an unlayered, unguarded `select { border: none; ... }`
 * rule (and a matching field-base mb-4 on every input/select) that beats
 * any @layer-scoped Tailwind utility regardless of specificity — border,
 * padding and margin are forced here with !important so these controls
 * reliably render, matching the fix already applied to the Schedule Site
 * Visit and Add Lead forms elsewhere in NEXCEO.
 */
.list-field {
  @apply text-sm bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg h-9 outline-none focus:ring-2 focus:ring-woot-500 transition-shadow;
  @apply border border-solid border-n-weak m-0 !important;
}
</style>
